import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Play } from "lucide-react"

type Props = {
  conceptId: string
  loop?: number
  title?: string
  className?: string
  // "concept" (default) plays the concept's rounds (Engine.start) - right
  // for showing off actual mission gameplay. "creation" jumps straight into
  // its De Makerij tool instead, skipping rounds entirely - right for a
  // concept whose interesting part IS the Makerij (e.g. paper-sketch),
  // where making someone play a mini-game first just delays the thing
  // that's actually being demoed.
  mode?: "concept" | "creation"
  // "overlay" (default): a play icon centered over a dark wash on the
  // preview itself - the original, self-contained affordance, used
  // wherever the teaser stands alone. "edge": no icon/wash on the preview
  // at all - the caller is putting its own "tap to play" affordance
  // somewhere else (the homepage "how it works" mobile carousel has its
  // own text callout, wired up via `ref`) and doesn't want this component's
  // own hint competing with it.
  playHint?: "overlay" | "edge"
}

// Exposed via `ref` so a caller can open the full-screen modal from its own
// UI (the "how it works" carousel's callout button lives outside this
// component entirely, per playHint="edge" above).
export type MissieDemoHandle = { open: () => void }

// The teaser used to render the iframe at 100%/100% of its own small box -
// the game inside has no idea it's in a teaser, so it just renders
// responsively for that literal (tiny) viewport, same as it would on an
// actual small phone: everything reads as huge and cramped, not "zoomed
// out." Rendering it at a real phone's own size, then scaling that whole
// result down to fit the box (the same trick app-store preview screenshots
// and video thumbnails use), gives an actual zoomed-out view instead.
const TEASER_FRAME_W = 375
const TEASER_FRAME_H = 667

// Same breakpoint the "how it works" layout itself switches on
// (.how-it-works-layout, AboutPage.css) - desktop has room to just let
// someone play the real thing right there; asking for a click first before
// showing anything interactive only made sense as a mobile-space-saving
// compromise, and reads as pointless friction anywhere there's room to
// spare (Laura: "I really just want inline playing on desktop... the
// overlay is horrible").
const DESKTOP_QUERY = "(min-width: 901px)"

const MissieDemo = forwardRef<MissieDemoHandle, Props>(function MissieDemo(
  {
    conceptId,
    loop = 1,
    title = "Techtok Missies, voorbeeldmissie",
    className = "",
    mode = "concept",
    playHint = "overlay",
  },
  ref
) {
  const [open, setOpen] = useState(false)
  useImperativeHandle(ref, () => ({ open: () => setOpen(true) }))
  const [teaserScale, setTeaserScale] = useState(1)
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia(DESKTOP_QUERY).matches
  )
  const teaserViewportRef = useRef<HTMLDivElement>(null)
  const src =
    mode === "creation"
      ? `/games/missies/index.html?preview=creation:${conceptId}`
      : `/games/missies/index.html?preview=concept:${conceptId}:${loop}`

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY)
    const update = () => setIsDesktop(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])

  useEffect(() => {
    if (isDesktop) return // the inline desktop frame is a fixed real size, nothing to scale
    const el = teaserViewportRef.current
    if (!el) return
    const update = () => {
      const rect = el.getBoundingClientRect()
      if (rect.width > 0) setTeaserScale(rect.width / TEASER_FRAME_W)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [isDesktop])

  useEffect(() => {
    if (!open) return
    // Plain overflow:hidden doesn't reliably lock scroll on iOS Safari -
    // touch-scroll can still leak through to the page behind a fixed
    // modal, and closing it can leave the page shifted from where it was.
    // Pinning the body itself at its current scroll offset (the standard
    // cross-browser body-scroll-lock technique) and restoring the real
    // scroll position on close is what actually guarantees "closing the
    // full-screen demo returns you to the exact spot on the homepage."
    const scrollY = window.scrollY
    const body = document.body.style
    const prev = { position: body.position, top: body.top, width: body.width, overflow: body.overflow }
    body.position = "fixed"
    body.top = `-${scrollY}px`
    body.width = "100%"
    body.overflow = "hidden"
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => {
      body.position = prev.position
      body.top = prev.top
      body.width = prev.width
      body.overflow = prev.overflow
      window.scrollTo(0, scrollY)
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  // Desktop: the real, fully interactive game right there, no click first -
  // same phone-frame look, just genuinely playable instead of a teaser.
  if (isDesktop) {
    return (
      <div className={`missie-demo-teaser missie-demo-inline ${className}`}>
        <div className="missie-demo-notch" aria-hidden="true" />
        <iframe src={src} title={title} className="missie-demo-inline-iframe" />
      </div>
    )
  }

  return (
    <>
      <div
        className={`missie-demo-teaser${playHint === "edge" ? " missie-demo-teaser--edge-play" : ""} ${className}`}
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen(true)
        }}
        aria-label="Speel de voorbeeldmissie, opent op volledig scherm"
      >
        <div className="missie-demo-notch" aria-hidden="true" />
        <div className="missie-demo-teaser-viewport" ref={teaserViewportRef}>
          <iframe
            src={src}
            title={title}
            tabIndex={-1}
            aria-hidden="true"
            className="missie-demo-teaser-iframe"
            style={{
              width: TEASER_FRAME_W,
              height: TEASER_FRAME_H,
              transform: `scale(${teaserScale})`,
            }}
          />
        </div>
        {/* A word-pill sitting dead-center over the preview read as heavy
            and blocked too much of what it's supposed to show off - a
            play-button icon is the same universally-understood "tap this"
            affordance video thumbnails and app previews already use
            everywhere, without needing to cover the content in text.
            Skipped entirely for playHint="edge" - that caller supplies its
            own affordance elsewhere and drives this via `ref`. */}
        {playHint !== "edge" && (
          <span className="missie-demo-play-button" aria-hidden="true">
            <Play fill="currentColor" strokeWidth={0} />
          </span>
        )}
      </div>

      {open &&
        createPortal(
          // Rendered straight into document.body, not in place here: a
          // squiggle section's CSS mask (used across the site for the wavy
          // section edges) creates a new containing block, which traps a
          // position:fixed element inside its own box instead of covering
          // the viewport. A portal sidesteps that ancestor entirely.
          <div className="missie-demo-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setOpen(false)}>
            <div className="missie-demo-modal" onClick={(e) => e.stopPropagation()}>
              <button className="missie-demo-modal-close" onClick={() => setOpen(false)} aria-label="Sluiten">
                ✕
              </button>
              <div className="missie-demo-modal-frame">
                <div className="missie-demo-notch" aria-hidden="true" />
                <iframe src={src} title={title} className="missie-demo-modal-iframe" />
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

export default MissieDemo
